import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { TextareaWithSubmit } from '@/components/composer/TextareaWithSubmit'
import { EmailTemplateSelectItems } from '@/components/EmailTemplateSelectItems'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { parseError } from '@/lib/errors'
import { type EmailTemplate, useEmailTemplates } from '@/models/emailTemplate'
import { type InsuranceCarrier, useSendCarrierSms } from '@/models/insuranceCarrier'
import { type SalesLead } from '@/models/salesLead'
import { markdownToPlainText } from '@/utils/markdownToPlainText'
import { fillDriverName } from '@/utils/templateVariables'

interface CarrierSmsComposerProps {
  lead: SalesLead
  carrier: InsuranceCarrier
  // Contact selected from the SMS button dropdown; seeds the recipient picker.
  initialContactId: string
  onSent: () => void
  onCancel: () => void
}

const SMS_SEGMENT_LEN = 160
const NO_TEMPLATE = '__none__'

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || 'there'
}

function renderTemplateForSms(template: EmailTemplate, driverName: string): string {
  return markdownToPlainText(fillDriverName(template.body, driverName))
}

export function CarrierSmsComposer({
  lead,
  carrier,
  initialContactId,
  onSent,
  onCancel,
}: CarrierSmsComposerProps) {
  const sendSms = useSendCarrierSms()
  const { data: templates = [] } = useEmailTemplates()
  const phoneContacts = carrier.contacts.filter((c) => c.phone)
  const [contactId, setContactId] = useState(initialContactId)
  const [body, setBody] = useState('')
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(NO_TEMPLATE)

  const primaryContact = lead.contacts.find((c) => c.isPrimary) ?? lead.contacts.at(0) ?? null
  const driverName = useMemo(() => firstName(primaryContact?.name ?? ''), [primaryContact?.name])

  if (phoneContacts.length === 0) {
    return <p className="text-muted-foreground text-sm">This carrier has no contact with a phone number.</p>
  }

  const contact = phoneContacts.find((c) => c.id === contactId) ?? phoneContacts[0]
  const segments = body.length === 0 ? 0 : Math.ceil(body.length / SMS_SEGMENT_LEN)

  function handleTemplateChange(id: string) {
    setSelectedTemplateId(id)
    if (id === NO_TEMPLATE) {
      setBody('')
      return
    }
    const template = templates.find((t) => t.id === id)
    if (template) setBody(renderTemplateForSms(template, driverName))
  }

  function handleSubmit(message: string) {
    sendSms.mutate(
      { leadId: lead.id, carrierId: carrier.id, contactId: contact.id, content: message },
      {
        onSuccess: () => {
          toast.success('SMS sent')
          setBody('')
          setSelectedTemplateId(NO_TEMPLATE)
          onSent()
        },
        onError: (err) => toast.error(parseError(err)),
      }
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground text-sm">Texting</span>
        <Select value={contact.id} onValueChange={setContactId} disabled={sendSms.isPending}>
          <SelectTrigger className="w-fit max-w-full text-sm">
            <SelectValue placeholder="Select contact" />
          </SelectTrigger>
          <SelectContent>
            {phoneContacts.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name} · {c.phone}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {templates.length > 0 && (
        <Select value={selectedTemplateId} onValueChange={handleTemplateChange} disabled={sendSms.isPending}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="No template" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_TEMPLATE}>No template</SelectItem>
            <EmailTemplateSelectItems templates={templates} />
          </SelectContent>
        </Select>
      )}
      <TextareaWithSubmit
        value={body}
        onValueChange={setBody}
        placeholder="Write your message…"
        onSubmitMessage={({ message }) => handleSubmit(message)}
        buttonProps={{ disabled: sendSms.isPending || !body.trim() }}
        addonChildren={
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        }
        leadingHint={
          <>
            {body.length} chars
            {segments > 0 && (
              <>
                {' · '}
                {segments} segment{segments === 1 ? '' : 's'}
              </>
            )}
          </>
        }
      />
    </div>
  )
}
